import React from 'react';
import { Box, Typography, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { Invoice } from '@/models/invoice';
import { CompanyConfig } from '@/models/config';
import '@/components/printPage/style.css';
import '@/components/printPage/font.css';
import './topsection.css';

interface TopSectionProps {
  invoice: Invoice;
  companyConfig: CompanyConfig;
}

const TopSection: React.FC<TopSectionProps> = ({ invoice, companyConfig }) => {
  // Format date to DD.MM.YYYY format
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch (error) {
      return '';
    }
  };

  // Split phone number into two lines if it contains multiple numbers
  const formatPhoneNumbers = (phone: string | undefined) => {
    if (!phone) return { first: '', second: '' };
    
    // Split by comma first (handles: "+91 123, +91 456" or "123, 456")
    const parts = phone.split(',').map(p => p.trim()).filter(p => p);
    
    if (parts.length >= 2) {
      // Extract numbers from each part (remove +91 prefix if present)
      const num1 = parts[0].replace(/^\+91\s*/i, '').trim().replace(/\s+/g, '');
      const num2 = parts[1].replace(/^\+91\s*/i, '').trim().replace(/\s+/g, '');
      return {
        first: num1,
        second: num2
      };
    }
    
    // Single part - check if it contains both known numbers
    let cleaned = phone.replace(/^\+91\s*/i, '').trim();
    
    if (cleaned.includes('9768802331') && cleaned.includes('7666412328')) {
      // Extract both numbers using regex
      const num1Match = cleaned.match(/(\d{10})/);
      const num2Match = cleaned.match(/(\d{10})/g);
      if (num2Match && num2Match.length >= 2) {
        return {
          first: num2Match[0],
          second: num2Match[1]
        };
      }
    }
    
    // Remove all spaces and check if it's two 10-digit numbers
    cleaned = cleaned.replace(/\s+/g, '');
    if (cleaned.length === 20 && /^\d{20}$/.test(cleaned)) {
      return {
        first: cleaned.substring(0, 10),
        second: cleaned.substring(10, 20)
      };
    }
    
    // Single number - remove spaces and return
    return {
      first: cleaned,
      second: ''
    };
  };

  // Format number with 2 decimal places
  const formatNumber = (num: number | undefined | null) => {
    if (num === undefined || num === null || isNaN(num)) {
      return '0.00';
    }
    return num.toFixed(2);
  };

  // Calculate amount for each product
  const calculateAmount = (quantity: number | undefined | null, rate: number | undefined | null) => {
    const qty = quantity || 0;
    const rte = rate || 0;
    return qty * rte;
  };

  // Get HSN list from API or calculate from products as fallback
  const getHSNList = () => {
    // Use hsn_list from API if available
    if (invoice.hsn_list && invoice.hsn_list.length > 0) {
      return invoice.hsn_list.map(hsnItem => ({
        hsn_code: hsnItem.hsn_code || '',
        quantity: hsnItem.quantity || 0,
        cgst: hsnItem.cgst || 0,
        sgst: hsnItem.sgst || 0,
        igst: hsnItem.igst || 0,
        total_amount: hsnItem.total_amount || 0,
        // Handle backward compatibility: calculate if missing
        total_taxable_amount: hsnItem.total_taxable_amount ?? 0,
        total_gst: hsnItem.total_gst ?? ((hsnItem.cgst || 0) + (hsnItem.sgst || 0) + (hsnItem.igst || 0))
      }));
    }
    
    // Fallback: Group products by HSN code for summary
    const hsnGroups: { [key: string]: { quantity: number; baseAmount: number; cgst: number; sgst: number; igst: number } } = {};
    
    invoice.products.forEach(productInfo => {
      const hsnCode = productInfo.product?.hsn_code || '';
      const quantity = productInfo.quantity || 0;
      const amount = calculateAmount(productInfo.quantity, productInfo.product?.rate);
      const gstRate = (productInfo.product as any)?.gst_percentage || (productInfo.product as any)?.gst_rate || 18;
      
      if (!hsnGroups[hsnCode]) {
        hsnGroups[hsnCode] = { quantity: 0, baseAmount: 0, cgst: 0, sgst: 0, igst: 0 };
      }
      
      hsnGroups[hsnCode].quantity += quantity;
      hsnGroups[hsnCode].baseAmount += amount;
      
      const gstAmount = (amount * gstRate) / 100;
      const customerState = (invoice.customer as any)?.state;
      if (customerState === 'Maharashtra') {
        hsnGroups[hsnCode].cgst += gstAmount / 2;
        hsnGroups[hsnCode].sgst += gstAmount / 2;
      } else {
        hsnGroups[hsnCode].igst += gstAmount;
      }
    });
    
    // Convert to array format
    return Object.entries(hsnGroups).map(([hsnCode, data]) => {
      const totalGst = data.cgst + data.sgst + data.igst;
      return {
        hsn_code: hsnCode,
        quantity: data.quantity,
        cgst: data.cgst,
        sgst: data.sgst,
        igst: data.igst,
        total_amount: totalGst, // Total GST amount
        total_taxable_amount: data.baseAmount, // Base amount before GST
        total_gst: totalGst // Total GST amount
      };
    });
  };

  const phoneNumbers = formatPhoneNumbers(companyConfig.mobile);
  const hsnList = getHSNList();

  // Calculate spacer rows between product table and HSN table
  // Total rows: 30
  // Product table rows: 1 header + product rows
  // HSN table rows: 1 header + HSN list rows
  // Spacer rows = 30 - product rows - HSN rows
  const productTableRows = 1 + invoice.products.length; // 1 header + data rows
  const hsnTableRows = 1 + hsnList.length; // 1 header + data rows
  const spacerRows = Math.max(0, 30 - productTableRows - hsnTableRows);

  return (
    <Box className="print-top-section">
      {/* Header Section */}
      <Box className="print-header">
        {/* Company Details Row - 15%-70%-15% split */}
        <Box className="print-company-row">
          {/* Left Section - Logo (15%) */}
          <Box className="print-logo-section">
            <img 
              src="/logo.png" 
              alt="Company Logo" 
              className="print-company-logo"
            />
          </Box>

          {/* Center Section - Document Title and Company Name (70%) */}
          <Box className="print-center-section">
            <Typography className="print-document-title print-text-sm print-font-primary print-uppercase print-text-center">
              CHALLAN CUM - TAX INVOICE
            </Typography>
            <Typography className="print-company-name print-text-5xl print-font-brand print-font-bold print-text-center">
              {companyConfig.name}
            </Typography>
          </Box>

          {/* Right Section - Contact Numbers (2 lines) */}
          <Box className="print-contact-section">
            {phoneNumbers.first && (
              <Typography className="print-contact-number print-font-primary print-text-right">
                Mobile : +91 {phoneNumbers.first}
            </Typography>
            )}
            {phoneNumbers.second && (
              <Typography className="print-contact-number print-font-primary print-text-right">
                +91 {phoneNumbers.second}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Address Row */}
        <Box className="print-address-row">
          <Typography className="print-business-description print-text-sm print-font-primary print-text-center">
            Manufacturers of :- All Kinds of Machinery Parts
          </Typography>
          <Typography className="print-factory-address print-text-sm print-font-primary print-text-center">
            Fact: J-17, Swastik Industrial Estate, Near Railway Phatak, Bhayander (East) - 401105.
          </Typography>
        </Box>

        {/* Separator Line */}
        <Divider className="print-address-separator" />

        {/* Invoice Details Blocks - Two Column Layout */}
        <Box className="print-invoice-details-blocks">
          {/* Left Column - Customer Details */}
          <Box className="print-customer-details-column">
            <Typography className="print-customer-name print-text-base print-font-primary print-font-semibold">
              To, {invoice.customer?.name || ''}
            </Typography>
            <Typography className="print-customer-address print-text-base print-font-primary">
              {[invoice.customer?.address_1, invoice.customer?.address_2].filter(Boolean).join(', ')}
            </Typography>
            <Typography className="print-customer-address print-text-base print-font-primary">
              {[invoice.customer?.address_3, invoice.customer?.address_4].filter(Boolean).join(', ')}
            </Typography>
            <Typography className="print-customer-address print-text-base print-font-primary">
              {invoice.customer?.city || ''}, {invoice.customer?.state || ''} - {invoice.customer?.pin || ''}
            </Typography>
            {invoice.customer?.gst_id && (
              <Typography className="print-customer-gst print-text-base print-font-primary">
                GST: {invoice.customer.gst_id}
              </Typography>
            )}
          </Box>

          {/* Vertical Separator */}
          <Box className="print-vertical-separator" />

          {/* Right Column - Invoice Meta Information */}
          <Box className="print-invoice-meta-column">
            <Box className="print-meta-row">
              <Typography className="print-meta-label print-text-base print-font-primary">Invoice No.:</Typography>
              <Typography className="print-meta-value print-text-base print-font-primary">{invoice.invoice_number || ''}</Typography>
              <Box className="print-meta-spacer-block" />
              <Typography className="print-meta-label print-text-base print-font-primary">Date:</Typography>
              <Typography className="print-meta-value print-text-base print-font-primary">{invoice.invoice_date ? formatDate(invoice.invoice_date) : ''}</Typography>
            </Box>
            <Box className="print-meta-row">
              <Typography className="print-meta-label print-text-base print-font-primary">Challan No.:</Typography>
              <Typography className="print-meta-value print-text-base print-font-primary">{invoice.invoice_number || ''}</Typography>
              <Box className="print-meta-spacer-block" />
              <Typography className="print-meta-label print-text-base print-font-primary">Date:</Typography>
              <Typography className="print-meta-value print-text-base print-font-primary">{invoice.invoice_date ? formatDate(invoice.invoice_date) : ''}</Typography>
            </Box>
            <Box className="print-meta-row">
              <Typography className="print-meta-label print-text-base print-font-primary">Order No.:</Typography>
              <Typography className="print-meta-value print-text-base print-font-primary">{invoice.order_number || ''}</Typography>
            </Box>
            <Box className="print-meta-row">
              <Typography className="print-meta-label print-text-base print-font-primary">Order Date:</Typography>
              <Typography className="print-meta-value print-text-base print-font-primary">{invoice.order_date ? formatDate(invoice.order_date) : ''}</Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Products Section */}
      <Box className="print-products">
        {/* Main Products Table */}
        <Box className="print-products-table-section">
          <TableContainer component={Paper} className="print-products-table">
            <Table>
              <TableHead>
                <TableRow className="print-table-header">
                  <TableCell className="print-header-cell print-col-srno print-text-base print-font-primary print-font-semibold">#</TableCell>
                  <TableCell className="print-header-cell print-col-hsn print-text-base print-font-primary print-font-semibold">HSN</TableCell>
                  <TableCell className="print-header-cell print-col-po print-text-base print-font-primary print-font-semibold">PO No.</TableCell>
                  <TableCell className="print-header-cell print-col-partno print-text-base print-font-primary print-font-semibold">Part No.</TableCell>
                  <TableCell className="print-header-cell print-col-description print-text-base print-font-primary print-font-semibold">Description</TableCell>
                  <TableCell className="print-header-cell print-col-qty print-text-base print-font-primary print-font-semibold print-text-right">Qty</TableCell>
                  <TableCell className="print-header-cell print-col-rate print-text-base print-font-primary print-font-semibold print-text-right">Rate</TableCell>
                  <TableCell className="print-header-cell print-col-gst print-text-base print-font-primary print-font-semibold print-text-right">GST %</TableCell>
                  <TableCell className="print-header-cell print-col-amount print-text-base print-font-primary print-font-semibold print-text-right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoice.products.map((productInfo, index) => (
                  <TableRow key={index} className="print-table-row">
                    <TableCell className="print-table-cell print-col-srno print-text-base print-font-primary">{index + 1}</TableCell>
                    <TableCell className="print-table-cell print-col-hsn print-text-base print-font-primary">
                      {productInfo.product?.hsn_code || ''}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-po print-text-base print-font-primary">
                      {productInfo.poNumber && productInfo.poNumber > 0 
                        ? productInfo.poNumber 
                        : (invoice.order_number || '-')}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-partno print-text-base print-font-primary">
                      {productInfo.product?.item_code || ''}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-description print-text-base print-font-primary">
                      {productInfo.product?.item_name || ''}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-qty print-text-base print-font-primary print-text-right">
                      {productInfo.quantity || 0}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-rate print-text-base print-font-primary print-text-right">
                      {formatNumber(productInfo.product?.rate)}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-gst print-text-base print-font-primary print-text-right">
                      {formatNumber((productInfo.product as any)?.gst_percentage || (productInfo.product as any)?.gst_rate || 18)}%
                    </TableCell>
                    <TableCell className="print-table-cell print-col-amount print-text-base print-font-primary print-text-right">
                      {formatNumber(calculateAmount(productInfo.quantity, productInfo.product?.rate))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Dynamic Spacers between Product Table and HSN Table */}
        {spacerRows > 0 && (
          <Box className="print-table-spacers">
            {Array.from({ length: spacerRows }).map((_, index) => (
              <Box key={index} className="print-table-spacer-row" />
            ))}
          </Box>
        )}

        {/* HSN Summary Table */}
        <Box className="print-hsn-summary-section">
          <TableContainer component={Paper} className="print-hsn-table">
            <Table>
              <TableHead>
                <TableRow className="print-table-header">
                  <TableCell className="print-header-cell print-col-hsn-code print-text-base print-font-primary print-font-semibold">HSN Code</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-qty print-text-base print-font-primary print-font-semibold print-text-right">Qty</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-cgst-pct print-text-base print-font-primary print-font-semibold print-text-right">CGST %</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-cgst print-text-base print-font-primary print-font-semibold print-text-right">CGST</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-sgst-pct print-text-base print-font-primary print-font-semibold print-text-right">SGST %</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-sgst print-text-base print-font-primary print-font-semibold print-text-right">SGST</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-igst-pct print-text-base print-font-primary print-font-semibold print-text-right">IGST %</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-igst print-text-base print-font-primary print-font-semibold print-text-right">IGST</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-taxable print-text-base print-font-primary print-font-semibold print-text-right">Taxable</TableCell>
                  <TableCell className="print-header-cell print-col-hsn-total print-text-base print-font-primary print-font-semibold print-text-right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {hsnList.map((hsnItem, index) => (
                  <TableRow key={index} className="print-table-row">
                    <TableCell className="print-table-cell print-col-hsn-code print-text-base print-font-primary">
                      {hsnItem.hsn_code || '-'}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-qty print-text-base print-font-primary print-text-right">
                      {hsnItem.quantity || 0}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-cgst-pct print-text-base print-font-primary print-text-right">
                      {hsnItem.cgst > 0 ? '9%' : '-'}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-cgst print-text-base print-font-primary print-text-right">
                      {hsnItem.cgst > 0 ? formatNumber(hsnItem.cgst) : '-'}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-sgst-pct print-text-base print-font-primary print-text-right">
                      {hsnItem.sgst > 0 ? '9%' : '-'}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-sgst print-text-base print-font-primary print-text-right">
                      {hsnItem.sgst > 0 ? formatNumber(hsnItem.sgst) : '-'}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-igst-pct print-text-base print-font-primary print-text-right">
                      {hsnItem.igst > 0 ? '18%' : '-'}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-igst print-text-base print-font-primary print-text-right">
                      {hsnItem.igst > 0 ? formatNumber(hsnItem.igst) : '-'}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-taxable print-text-base print-font-primary print-text-right">
                      {formatNumber(hsnItem.total_taxable_amount || 0)}
                    </TableCell>
                    <TableCell className="print-table-cell print-col-hsn-total print-text-base print-font-primary print-text-right">
                      {formatNumber(hsnItem.total_amount || 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default TopSection;
